import { ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IconProps } from '@components/icons';
import { IconCopy } from '@components/icons/copy';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconHire } from '@components/icons/hire';
import { IconMove } from '@components/icons/move';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconReject } from '@components/icons/reject';
import { IconTrash } from '@components/icons/trash';
import { IconUndo } from '@components/icons/undo';

import { useFavoriteActionItem } from 'components/navigation/favorites';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import {
  applicationBaseUrl,
  applicationCopyUrl,
  applicationMoveUrl,
  applicationOnboardingUrl,
  jobPipelineUrl,
} from 'features/routing';
import { useStore } from 'models/Provider';
import { IApplication } from 'models/types';

import { useDeleteApplicantsAccess } from './use-delete-applicants-access';
import { useUnHireApplication } from './use-unhire-application';
import { useUnRetractApplication } from './use-unretract-application';

type ApplicationOption = {
  label: string;
  onSelect: (item: IApplication) => void;
  variant?: 'base' | 'danger';
  icon?: ReactElement<IconProps>;
  isDisabled?: (item: IApplication) => boolean;
  tooltip?: (item: IApplication) => string | undefined;
};

export function useApplicationOptions() {
  const { t } = useStore();
  const { showModal } = useModal();
  const unHireApplication = useUnHireApplication();
  const unRetractApplication = useUnRetractApplication();

  const navigate = useNavigate();
  const location = useLocation();
  const { hasRolePermission } = usePermissions();
  const deleteAccessFor = useDeleteApplicantsAccess();

  const getFavoriteAction = useFavoriteActionItem('application');

  const actions: Record<string, (application: IApplication) => void> = useMemo(
    () => ({
      edit: (application) => {
        showModal('EDIT_APPLICANT', {
          _container: {
            closeOnOverlayClickOrEsc: false,
          },
          applicationId: application.id,
        });
      },

      openAccessModal: (application) => {
        showModal('ADD_APPLICATION_ACCESS', {
          applicationId: application.id,
        });
      },

      copy: (application) => {
        navigate(applicationCopyUrl(application.id, application.jobId), {
          state: { onCloseUrl: location.pathname },
        });
      },

      move: (application) => {
        navigate(applicationMoveUrl(application.id, application.jobId), {
          state: { onCloseUrl: location.pathname },
        });
      },

      openRemoveModal: (application) => {
        showModal('REMOVE_APPLICATION', {
          _container: {
            minWidth: '200px',
            autoWidth: true,
          },
          applicationName: application.name,
          applicationId: application.id,
          redirectUrl: location.pathname.includes(
            applicationBaseUrl(application.id, application.jobId),
          )
            ? jobPipelineUrl(application.job.id)
            : undefined,
        });
      },

      openRejectModal: (application) => {
        // Component isn't typed so doesn't know `applicants` and `onSuccess` aren't required.
        // We can remove them once the component is typed.
        // @ts-expect-error
        showModal('REJECT_APPLICANTS', {
          _container: {
            width: '37.5rem',
          },
          applicant: application,
        });
      },

      openRetractModal: (application) => {
        showModal('RETRACT_APPLICANT', {
          _container: {
            autoWidth: false,
          },
          applicationId: application.id,
          applicationName: application.name,
        });
      },

      openOnboardModal: (application) => {
        showModal('ONBOARD_APPLICANT', {
          _container: {
            autoWidth: false,
          },
          applicationId: application.id,
          jobId: application.jobId,
        });
      },

      openHireModal: (application) => {
        showModal('HIRE_APPLICANT', {
          _container: {
            autoWidth: false,
          },
          applicationId: application.id,
          applicationName: application.name,
          jobId: application.jobId,
          openOnboardModal: true,
        });
      },

      sendEmail: (application) => {
        if (application.email) {
          window.location.href = `mailto:${application.email}`;
        }
      },
    }),
    [location.pathname, navigate, showModal],
  );

  return useCallback(
    (application: IApplication) => {
      let options: Array<ApplicationOption | 'separator'> = [];
      if (!hasRolePermission('application:view-options')) return options;

      if (hasRolePermission('application:edit')) {
        options.push({
          icon: <IconEdit />,
          label: t('application.options.edit'),
          onSelect: () => actions.edit(application),
        });
      }

      if (hasRolePermission('application:grant-access')) {
        options.push({
          icon: <IconPersonAdd />,
          label: t('application.options.access'),
          onSelect: () => actions.openAccessModal(application),
        });
      }

      options.push({
        icon: <IconCopy />,
        label: t('application.options.copy'),
        onSelect: () => actions.copy(application),
      });

      if (hasRolePermission('application:move')) {
        options.push({
          icon: <IconMove />,
          label: t('application.options.move'),
          onSelect: () => actions.move(application),
        });
      }

      if (application.email) {
        options.push({
          icon: <IconEmail />,
          label: t('application.options.email'),
          onSelect: () => actions.sendEmail(application),
        });
      }

      const favoriteItem = getFavoriteAction(application.id);
      if (favoriteItem) {
        options.push(favoriteItem);
      }

      if (
        hasRolePermission('application:retract') ||
        hasRolePermission('application:hire') ||
        hasRolePermission('application:reject')
      ) {
        options.push('separator');
      }

      if (
        !application.isRejected &&
        !application.isRetracted &&
        hasRolePermission('application:hire')
      ) {
        options.push({
          icon: application.isHired ? <IconUndo /> : <IconHire />,
          label: t(`application.options.${application.isHired ? 'unhire' : 'hire'}`),
          onSelect: application.isHired
            ? () => unHireApplication.mutate({ applicationId: application.id })
            : () => actions.openHireModal(application),
        });
      }

      if (
        !application.isHired &&
        !application.isRetracted &&
        hasRolePermission('application:reject')
      ) {
        options.push({
          variant: application.isRejected ? 'base' : 'danger',
          onSelect: application.isRejected
            ? application.unReject
            : () => actions.openRejectModal(application),
          icon: application.isRejected ? <IconUndo /> : <IconReject />,
          label: t(`application.options.${application.isRejected ? 'unreject' : 'reject'}`),
        });
      }

      if (
        !application.isHired &&
        !application.isRejected &&
        hasRolePermission('application:retract')
      ) {
        options.push({
          onSelect: application.isRetracted
            ? () => unRetractApplication.mutate({ applicationId: application.id })
            : () => actions.openRetractModal(application),
          icon: <IconUndo />,
          label: t(`application.options.${application.isRetracted ? 'unretract' : 'retract'}`),
        });
      }

      if (application.isHired) {
        options.push({
          icon: <IconOnboarding />,
          onSelect: () => navigate(applicationOnboardingUrl(application.id, application.jobId)),
          label: t('application.options.onboard'),
        });
      }

      const deleteAccess = deleteAccessFor(application.job.createdById?.id);
      if (deleteAccess.isVisible) {
        options.push('separator');
        options.push({
          variant: 'danger',
          icon: <IconTrash />,
          onSelect: () => actions.openRemoveModal(application),
          label: t('application.options.delete'),
          isDisabled: () => deleteAccess.isDisabled,
          tooltip: () => deleteAccess.tooltip,
        });
      }

      return options;
    },
    [
      actions,
      hasRolePermission,
      t,
      navigate,
      unHireApplication,
      unRetractApplication,
      getFavoriteAction,
      deleteAccessFor,
    ],
  );
}
